<template>
  <div class="card" :class="widgetClasses">
    <div class="card-header border-0 pt-5">
      <h3 class="card-title align-items-start flex-column">
        <span class="card-label fw-bold fs-3 mb-1">Faturamentos</span>
        <span class="text-muted fw-semobold fs-7"
          >Faturamentos realizados no período</span
        >
      </h3>
      <div class="card-toolbar">
        <button
          type="button"
          class="btn btn-sm btn-icon btn-color-primary btn-active-light-primary"
          data-toggle="tooltip"
          data-placement="top"
          title="Atualizar"
          @click="this.atualizarGrafico()"
        >
          <span class="svg-icon svg-icon-2">
            <inline-svg src="media/icons/duotune/arrows/arr029.svg" />
          </span>
        </button>
      </div>
    </div>
    <div class="card-body">
      <apexchart
        v-if="!this.loading"
        ref="graficoFaturamento"
        class="mixed-widget-10-chart"
        :options="options"
        :series="series"
        :height="chartHeight"
      ></apexchart>
      <div v-if="loading" v-bind:data-kt-indicator="loading ? 'on' : 'off'">
        <span class="indicator-progress">
          carregando...
          <span
            class="spinner-border spinner-border-sm align-middle ms-2"
          ></span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { getCSSVariableValue } from "@/assets/ts/_utils";
import numeral from "numeral";

export default {
  components: {},
  props: {
    widgetColor: { type: String, required: false, default: "danger" },
    widgetClasses: {
      type: String,
      required: false,
      default: "card-xl-stretch mb-xl-8",
    },
    chartHeight: { type: Number, required: false, default: 0 },
    chartColor: { type: String, required: false, default: "primary" },
  },
  methods: {
    async atualizarGrafico() {
      this.loading = true;
      this.series = [];
      const mesesComMovimentacao = [];
      try {
        const meses = [
          "Jan",
          "Fev",
          "Mar",
          "Abr",
          "Mai",
          "Jun",
          "Jul",
          "Ago",
          "Set",
          "Out",
          "Nov",
          "Dez",
        ];

        let retFaturamentos = await this.$store.dispatch(
          "faturamento/getFaturamentoOData",
          "?$apply=filter(cancelado eq '0')/compute(month(data_emissao) as mes)/groupby((mes), aggregate(total with sum as total))&$select=mes,total"
        );

        if (retFaturamentos && retFaturamentos !== null) {
          retFaturamentos.forEach((item) => {
            if (!mesesComMovimentacao.includes(item.mes)) {
              mesesComMovimentacao.push(item.mes);
            }
          });

          this.series.push({
            name: "Faturado",
            data: retFaturamentos.map((item) => item.total),
          });
        }

        let retFaturamentosCancelados = await this.$store.dispatch(
          "faturamento/getFaturamentoOData",
          "?$apply=filter(cancelado eq '1')/compute(month(data_emissao) as mes)/groupby((mes), aggregate(total with sum as total))&$select=mes,total"
        );

        if (retFaturamentosCancelados && retFaturamentosCancelados !== null) {
          retFaturamentosCancelados.forEach((item) => {
            if (!mesesComMovimentacao.includes(item.mes)) {
              mesesComMovimentacao.push(item.mes);
            }
          });

          this.series.push({
            name: "Cancelado",
            data: retFaturamentosCancelados.map((item) => item.total),
          });
        }

        if (mesesComMovimentacao && mesesComMovimentacao.length > 0) {
          this.options.xaxis.categories = mesesComMovimentacao.map(
            (mes) => meses[mes - 1]
          );
        }
      } catch (erro) {
        console.log(erro);
      }
      this.loading = false;
    },
  },
  async mounted() {
    const color = this.chartColor;
    const labelColor = getCSSVariableValue("--kt-gray-500");
    const borderColor = getCSSVariableValue("--kt-gray-200");
    const secondaryColor = getCSSVariableValue("--kt-danger");
    const baseColor = getCSSVariableValue(`--kt-${color}`);

    this.options.chart.height = this.chartHeight;
    this.options.xaxis.labels.style.colors = labelColor;
    this.options.yaxis.labels.style.colors = labelColor;
    this.options.colors = [baseColor, secondaryColor];
    this.options.grid.borderColor = borderColor;

    await this.atualizarGrafico();
  },
  data() {
    return {
      loading: false,
      baseColor: "",
      series: [],
      height: 0,
      options: {
        chart: {
          fontFamily: "inherit",
          type: "bar",
          height: "auto",
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "45%",
          },
        },
        legend: {
          show: false,
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"],
        },
        xaxis: {
          categories: [],
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          labels: {
            style: {
              colors: "--kt-gray-500",
              fontSize: "12px",
            },
          },
        },
        yaxis: {
          labels: {
            style: {
              colors: "--kt-gray-500",
              fontSize: "12px",
            },
            formatter: function (value) {
              return new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(value);
            },
          },
        },
        fill: {
          type: "solid",
        },
        states: {
          normal: {
            filter: {
              type: "none",
              value: 0,
            },
          },
          hover: {
            filter: {
              type: "none",
              value: 0,
            },
          },
          active: {
            allowMultipleDataPointsSelection: false,
            filter: {
              type: "none",
              value: 0,
            },
          },
        },
        tooltip: {
          style: {
            fontSize: "12px",
          },
          y: {
            formatter: function (val) {
              return "R$ " + numeral(val).format("0.00");
            },
          },
        },
        colors: ["--kt-gray-500", "--kt-gray-300"],
        grid: {
          padding: {
            top: 10,
          },
          borderColor: "--kt-gray-500",
          strokeDashArray: 4,
          yaxis: {
            lines: {
              show: true,
            },
          },
        },
      },
    };
  },
};
</script>

<style lang="scss" scoped></style>
